import { BarChart3, Coins, MessageSquare, Send, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { BarChartVertical, ChartCard } from "@/components/charts";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { SectionCard } from "@/components/ui/section-card";
import { StatCard } from "@/components/ui/stat-card";
import { DisabledBanner } from "@/features/observability/components/DisabledBanner";
import {
  useMeUsage,
  useObservabilityStatus,
} from "@/features/observability/hooks/useObservability";
import { formatCostUsd } from "@/lib/cost";

export function MeUsagePage() {
  const { t } = useTranslation("observability");
  const status = useObservabilityStatus();
  const me = useMeUsage();

  const costByDayData = useMemo(
    () =>
      (me.data?.cost_by_day ?? []).map((d) => ({
        label: d.date.slice(5),
        value: d.cost,
      })),
    [me.data?.cost_by_day],
  );

  if (status.isLoading) return <LoadingState />;
  if (status.isError)
    return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  if (status.data && !status.data.enabled) {
    return <DisabledBanner reason={status.data.reason} />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-brand-500" /> {t("nav.me")}
          </span>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("kpi.totalCost")}
          value={me.data ? formatCostUsd(me.data.total_cost) : "—"}
          icon={Coins}
          loading={me.isLoading}
        />
        <StatCard
          label={t("kpi.totalRequests")}
          value={me.data?.total_requests.toLocaleString("pt-BR") ?? "—"}
          icon={Send}
          loading={me.isLoading}
        />
        <StatCard
          label={t("kpi.totalTokens")}
          value={me.data?.total_tokens.toLocaleString("pt-BR") ?? "—"}
          icon={Sparkles}
          loading={me.isLoading}
        />
        <StatCard
          label={t("kpi.activeThreads")}
          value={me.data?.active_threads.toLocaleString("pt-BR") ?? "—"}
          icon={MessageSquare}
          loading={me.isLoading}
        />
      </div>

      <ChartCard
        title={t("charts.costByDay")}
        height={260}
        loading={me.isLoading}
        isEmpty={!me.isLoading && costByDayData.length === 0}
        emptyMessage={t("empty.noData")}
      >
        <BarChartVertical data={costByDayData} valueFormatter={formatCostUsd} />
      </ChartCard>

      {me.data && me.data.expensive_threads.length > 0 ? (
        <SectionCard title={t("charts.expensiveThreads")}>
          <ul className="space-y-1 text-xs text-ink-900">
            {me.data.expensive_threads.map((tt) => {
              const label = tt.title?.trim() || t("empty.untitled");
              return (
                <li
                  key={tt.thread_id}
                  className="flex justify-between gap-3 border-b border-line py-1"
                >
                  <span className="truncate" title={label}>
                    {label}
                  </span>
                  <span className="tabular shrink-0">
                    {formatCostUsd(tt.cost)}
                  </span>
                </li>
              );
            })}
          </ul>
        </SectionCard>
      ) : null}
    </div>
  );
}
